import { getLogger } from '../../logging';
import { assertNever } from '../../typeguards';
const logger = getLogger('drive');
export const getResourceIdentifierFromItem = item => {
  if (item?.driveMetadata?.driveType === 'transient') {
    // Transient drive "resources" are not real, thus the typical approach will result in an identifier
    // that is useless for retrieving anything. Instead return the standard identifier.
    return getIdentifierFromItem(item);
  } else {
    return {
      type: 'driveItemId',
      value: item.resource.id
    };
  }
};

/**
 * Extract the proper ItemIdentifier from an item
 * @param item
 */
export const getIdentifierFromItem = item => {
  switch (item.viewType) {
    case 'common':
    case 'shortcut':
    case 'commonReference':
      return {
        type: 'driveItemId',
        value: item.id
      };
    case 'drive':
      if (!item.driveMetadata) {
        throw new Error(`Item with viewType of "drive" but no driveMetadata is invalid`);
      }
      switch (item.driveMetadata.driveType) {
        case 'persistent':
          if (!item.driveMetadata.persistentType) {
            throw new Error(`Persistent drive with no persistent drive is invalid`);
          }
          return {
            type: 'persistentLocation',
            value: item.driveMetadata.persistentType
          };
        case 'transient':
          if (item.driveMetadata.transientType !== 'search') {
            throw new Error(`Non-search transient drives are not yet supported`);
          }
          if (!item.driveMetadata.filter) {
            throw new Error(`Search transient drive with no "filter" value is invalid`);
          }
          return {
            type: 'searchLocation',
            value: {
              name: item.name,
              searchQuery: item.driveMetadata.filter,
              idQualifier: item.driveMetadata.idQualifier
            }
          };
        default:
          return assertNever(item.driveMetadata.driveType);
      }
    default:
      return assertNever(item.viewType);
  }
};

/**
 * Array version of getIdentifierFromitem
 * @param items
 */
export const getIdentifiersFromItems = items => {
  return items.map(item => getIdentifierFromItem(item));
};
export const getIdentifierFromAncestor = ancestor => {
  return ancestor.id === 'drive-root' ? {
    type: 'persistentLocation',
    value: 'root'
  } : {
    type: 'driveItemId',
    value: ancestor.id
  };
};
export const getIdentifiersFromAncestors = ancestors => {
  return ancestors.map(ancestor => getIdentifierFromAncestor(ancestor));
};

//
// Drive component specific calls/helpers
//

/**
 * ItemIdentifiers are meant to be flexible in allowing consumers to specify content in whatever
 * way is easiest. This means the same content could be referred to by multiple ItemIdentifiers of
 * different formats. This can be a challenge when it comes to matching up two ItemIdentifiers.
 *
 * However, every type should be convertible to the base type "driveItemId", which is the guaranteed
 * unique id from the drive service, which allows for a normalized comparison. This function does
 * that conversion.
 * @param identifier
 */
export const toDriveItemIdentifier = identifier => {
  switch (identifier.type) {
    case 'driveItemId':
    case 'folderUri':
      return {
        type: 'driveItemId',
        value: identifier.value
      };
    case 'persistentLocation':
      return {
        type: 'driveItemId',
        value: `drive-${identifier.value}`
      };
    case 'searchLocation':
      // Technically the id for a transient search item is "drive-search-{idQualifier}". However, that
      // cannot be used to fetch anything. Instead, use "transient?driveConfig={driveConfig}",
      // which will properly fetch the item when inserted into "/drive/items/{value}"
      return {
        type: 'driveItemId',
        value: `transient?driveConfig=${JSON.stringify({
          filter: identifier.value.searchQuery,
          idQualifier: identifier.value.idQualifier,
          name: identifier.value.name,
          transientType: 'search'
        })}`
      };
    case 'customLocation':
    case 'customServiceItem':
    case 'customItem':
      const error = new Error(`toDriveItemIdentifier called with an custom ItemIdentifier. This is not supported and should probably be handled by the custom ContentDataProvider associated with the identifier`);
      logger.error(error.message);
      throw error;
    default:
      assertNever(identifier);
  }
};

/**
 * Given two ItemIdentifiers, compare their normalized values to determine if they reference the same
 * content.
 * @param a
 * @param b
 */
export const doIdentifiersMatch = (a, b) => {
  // If we encounter any unknown identifiers, likely from a custom data provider, return false
  if (!isKnownIdentifier(a) || !isKnownIdentifier(b)) {
    return false;
  }
  const normalizedA = toDriveItemIdentifier(a);
  const normalizedB = toDriveItemIdentifier(b);
  if (normalizedA.value === normalizedB.value) {
    return true;
  }
  if (a.type === 'persistentLocation' && b.type === 'persistentLocation' || a.type !== 'persistentLocation' && b.type !== 'persistentLocation') {
    return false;
  }
  return itemIdToUriIfApplicable(normalizedA.value) === itemIdToUriIfApplicable(normalizedB.value);
};

/**
 * Map of persistentDrive id's to their resource uris for matching
 * persistent drives with backing folder resource to their folder uri.
 */
const peristentLocationIdsToURIs = new Map();

/**
 * Populates a persisted mapping of persistent drive ids to their
 * resource uri.
 * @param persistentDrives
 */
export const populatePersistentLocationIdToURIMap = persistentDrives => {
  for (const persistentDrive of persistentDrives) {
    if (persistentDrive.id !== persistentDrive.resource.id) {
      peristentLocationIdsToURIs.set(persistentDrive.id, persistentDrive.resource.id);
      if (persistentDrive.id.startsWith('drive-')) {
        peristentLocationIdsToURIs.set(persistentDrive.id.substring(6), persistentDrive.resource.id);
      }
    }
  }
};

/**
 * Returns the resource uri for item ids that are persistent drive ids. If the item id
 * is not a persistent drive id, then the the item id is returned.
 * @param itemIdentifierValue
 */
export const itemIdToUriIfApplicable = itemIdentifierValue => {
  const uri = peristentLocationIdsToURIs.get(itemIdentifierValue);
  return uri ? uri : itemIdentifierValue;
};

/**
 * Returns the persistent location value, if applicable, if the given uriValue is the the uri of a persistent
 * drive item. If the given uriValue is not that of aa persistent drive item, then empty string returned.
 * @param uriValue
 */
export const uriToPersistentLocationIfApplicable = uriValue => {
  if (uriValue.startsWith('drive-')) {
    return uriValue.substring(6);
  }
  for (const [key, value] of peristentLocationIdsToURIs) {
    if (value === uriValue) {
      if (key.startsWith('drive-')) {
        return key.substring(6);
      }
      return key;
    }
  }
  return '';
};

/**
 * Given an Item and an ItemIdentifier, determine if the reference the same underlying sas-drive Item
 * @param item
 * @param identifier
 * @returns
 */
export const doesItemMatchIdentifier = (item, identifier) => {
  const itemIdentifier = getIdentifierFromItem(item);
  return doIdentifiersMatch(itemIdentifier, identifier);
};
export const getServiceIdFromIdentifier = identifier => {
  const serviceIdentifier = toDriveItemIdentifier(identifier);
  return serviceIdentifier.value;
};

/**
 * Returns whether obj is an Item
 * @param obj
 * @returns
 */
export const isItem = obj => typeof obj.id === 'string' && typeof obj.name === 'string' && typeof obj.viewType === 'string' && (!obj.Links || obj.links instanceof Array) && obj.resource instanceof Object;
const isKnownIdentifier = identifier => {
  return identifier.type === 'driveItemId' || identifier.type === 'folderUri' || identifier.type === 'persistentLocation' || identifier.type === 'searchLocation';
};
const LOCATIONS_TO_AREA_TYPESETS = new Map([['root', 'folderArea'], ['myFolder', 'folderArea'], ['history', 'recentsArea'], ['favorites', 'favoritesArea'], ['quarantined', 'quarantinedArea'], ['quickAccess', 'quickAccessArea'], ['recommendations', 'recommendationsArea'], ['trash', 'recycleBinArea'], ['sharedByMe', 'sharedByArea'], ['sharedWithMe', 'sharedWithArea']]);
const LOCATIONS_TO_AREA_NEW_TYPESETS = new Map([['root', 'folderAreaNew'], ['myFolder', 'folderAreaNew'], ['history', 'recentsAreaNew'], ['favorites', 'favoritesAreaNew'], ['quarantined', 'quarantinedAreaNew'], ['quickAccess', 'quickAccessAreaNew'], ['recommendations', 'recommendationsAreaNew'], ['trash', 'recycleBinAreaNew'], ['sharedByMe', 'sharedByAreaNew'], ['sharedWithMe', 'sharedWithAreaNew']]);
const LOCATIONS_TO_ITEMS_TYPESETS = new Map([['root', 'folderItems'], ['myFolder', 'folderItems'], ['history', 'recentsItems'], ['favorites', 'favoritesItems'], ['quarantined', 'quarantinedItems'], ['quickAccess', 'quickAccessItems'], ['recommendations', 'recommendationsItems'], ['trash', 'recycleBinItems'], ['sharedByMe', 'sharedByItems'], ['sharedWithMe', 'sharedWithItems']]);
export const getItemsActionTypeSet = rootItemIdentifier => {
  logger.info(`getItemsActionTypeSet itemIdentifier: ${JSON.stringify(rootItemIdentifier)}`);
  if (!rootItemIdentifier) {
    return 'folderItems';
  } else if (rootItemIdentifier.type === 'persistentLocation') {
    const itemsTypeSet = LOCATIONS_TO_ITEMS_TYPESETS.get(rootItemIdentifier.value);
    return itemsTypeSet ? itemsTypeSet : 'folderItems';
  } else if (rootItemIdentifier.type === 'searchLocation') {
    return 'searchItems';
  }
  return 'folderItems';
};
export const getAreaActionTypeSet = rootItemIdentifier => {
  logger.info(`getAreaActionTypeSet itemIdentifier: ${JSON.stringify(rootItemIdentifier)}`);
  if (!rootItemIdentifier) {
    return 'folderArea';
  } else if (rootItemIdentifier.type === 'persistentLocation') {
    const itemsTypeSet = LOCATIONS_TO_AREA_TYPESETS.get(rootItemIdentifier.value);
    return itemsTypeSet ? itemsTypeSet : 'folderArea';
  } else if (rootItemIdentifier.type === 'searchLocation') {
    return 'searchArea';
  }
  return 'folderArea';
};
export const getAreaNewActionTypeSet = rootItemIdentifier => {
  logger.info(`getAreaNewActionTypeSet itemIdentifier: ${JSON.stringify(rootItemIdentifier)}`);
  if (!rootItemIdentifier) {
    return 'folderAreaNew';
  } else if (rootItemIdentifier.type === 'persistentLocation') {
    const itemsTypeSet = LOCATIONS_TO_AREA_NEW_TYPESETS.get(rootItemIdentifier.value);
    return itemsTypeSet ? itemsTypeSet : 'folderArea';
  } else if (rootItemIdentifier.type === 'searchLocation') {
    return 'searchAreaNew';
  }
  return 'folderAreaNew';
};
//# sourceMappingURL=util.js.map